import { useState } from 'react'
import Coverart from '@components/app/coverart'
import Share from '@components/app/share'
import Waveform from '@components/app/waveform'
import Spinner from '@components/common/spinner'
import BuyModal from '@components/modals/buy'
import UIHiresBadge from '@components/ui/hires'
import UISample from '@components/ui/sample'
import PlayerCtx from '@data/contexts/player'
import PlayBtn from '@pages/media/album/play'
import { timenize } from '@utils/string'
import { formatDate } from '@utils/date'
import Link from 'next/link'
import { FormattedMessage } from 'react-intl'
import Showmore from '@/components/app/show-more'
import { AlbumProps } from '../../../../types/album'
import { API_HOST } from '../../../config'
import Userarea from './userarea'
import styles from './media.module.scss'

interface Props {
  album: AlbumProps
}

const AlbumMedia = ({ album }: Props) => {
  // const [ currentSound, setCurrentSound ] = useState(0)
  const [ isBuyOpen, setBuyOpen ] = useState(false)
  const [ isShareOpen, setShareOpen ] = useState(false)

  const {
    isLoading,
    isPlaying,
    nowPlaying,
    play,
    pause,
    setPlaylist,
  } = PlayerCtx.useContainer()

  const isThis =
    album.sounds.filter((s) => s.id === nowPlaying.track?.id).length > 0
  const currentSound = isThis
    ? album.sounds.findIndex((s) => s.id === nowPlaying.track?.id)
    : 0

  const onTrackClick = (index) => {
    if (isThis && isPlaying && currentSound === index) {
      pause()
      return
    }

    const list = album.sounds.map((s) => ({
      id: s.id,
      albumId: album.id,
      creatorId: album.user.id,
      title: s.title,
      slug: s.slug,
      user: album.user.name,
      coverart: s.coverart_crop_url,
      waveformUrl: s.waveform_url,
      duration: s.metadata?.duration ?? 0,
      webmUrl: `${API_HOST}/sounds/${s.id}.webm`,
      mp3Url: `${API_HOST}/sounds/${s.id}.mp3`,
      hlsUrl: `${API_HOST}/sounds/${s.id}.m3u8`,
      limit: s.limit,
      limitOn: s.limitOn,
    }))

    if (!isThis) {
      setPlaylist({
        name: album.title,
        path: `album/${album.id}`,
        list,
      })
    }

    play(list[index])
  }

  const href = `/album/${album.id}/${album.slug}`

  return (
    <div className={`${styles.media} mb-5`}>
      <div className="d-flex mb-1">
        <div className={styles.coverartContainer}>
          <div className="ps-relative">
            <Link
              href={href}
              aria-label={`More detail about ${album.title}`}
            >
              <figure className={styles.coverartFigure}>
                <Coverart
                  src={album.coverart_crop_url}
                  alt={album.title}
                  width={120}
                />
              </figure>
            </Link>
            <PlayBtn
              size="2rem"
              className={styles.playOnCover}
              album={album}
              index={currentSound}
              isAll
            />
          </div>
        </div>
        <div className={styles.detailContainer}>
          <h3 className="tx-22 mb-2">
            <Link
              href={href}
              className="alt"
            >
              {album.title}
            </Link>
          </h3>
          <p className="tx-14">
            <Link
              href={href}
              className="nohover tx-gish-light"
            >
              {album.description ? (
                <>
                  {album.description.substr(0, 100)}
                  {album.description.length > 100 && '...'}
                </>
              ) : (
                <>
                  Released&nbsp;{formatDate(album.released_at)}
                </>
              )}
            </Link>
          </p>
          <div className="tx-12 mb-3">
            <Userarea user={album.user} />
            {/* <span className="ml-3">released</span> */}
            <span className="dot" />
            <time dateTime={album.released_at.toString()}>
              {formatDate(album.released_at)}
            </time>
          </div>
          <Waveform
            id={album.sounds[currentSound].id}
            duration={album.sounds[currentSound].metadata?.duration ?? 0}
            src={album.sounds[currentSound].waveform_url}
            static={album.sounds[currentSound].limitOn}
          />
          <div className={`${styles.actions} ${styles.relative}`}>
            <button
              type="button"
              className="btn-chromeless tx-black tx-12 mr-3"
              onClick={
                isShareOpen
                  ? () => setShareOpen(false)
                  : () => setShareOpen(true)
              }
            >
              <i className="solicon-share mr-2" />
              {isShareOpen ? (
                <FormattedMessage
                  id="app.stopshare"
                  defaultMessage="Stop Sharing"
                />
              ) : (
                <FormattedMessage id="app.share" defaultMessage="Share" />
              )}
            </button>
            <div
              className={
                isShareOpen
                  ? `${styles.share} ${styles.show}`
                  : `${styles.share} ${styles.hide}`
              }
            >
              <Share
                type="album"
                id={album.id}
                url={`https://sdn.lv/a${album.id}`}
                text={`${album.title} by ${album.user.name}`}
              />
            </div>
            {album.onSale && (
              <button
                type="button"
                className="btn-chromeless tx-black tx-12 mr-3"
                onClick={() => setBuyOpen(true)}
              >
                <i className="solicon-tag-cord mr-2" />
                <FormattedMessage
                  id="app.buy.album"
                  defaultMessage="Buy this album"
                />
              </button>
            )}
          </div>
        </div>
      </div>
      <div className={styles.soundList}>
        <Showmore defaultHeight={130}>
          <ol>
            {album.sounds.map((sound, i) => (
              <li key={sound.id} className="tx-14">
                <button
                  type="button"
                  className="btn-chromeless d-flex align-items-center pr-2"
                  onClick={() => onTrackClick(i)}
                >
                  <span className="flex0">
                    {isThis && currentSound === i && isPlaying ? (
                      <>
                        {isLoading ? (
                          <span className={styles.playing}>
                            <Spinner size={14} />
                          </span>
                        ) : (
                          <span
                            className={`tx-oswald tx-w400 tx-gish tx-12 ${styles.playing}`}
                          >
                            <span className="solicon-speaker" />
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <span
                          className={`tx-oswald tx-w400 tx-g700 tx-12 ${styles.index}`}
                        >
                          {i + 1}.
                        </span>
                        <span className={styles.play}>
                          <img src="/static/images/btn-play.svg" alt="play" />
                        </span>
                      </>
                    )}
                  </span>
                  <span className="flex1 tx-ellipsis mr-2">{sound.title}</span>
                  {sound.hi_resolution && <UIHiresBadge className="mr-2" />}
                  {sound.limitOn && <UISample className="mr-2" />}
                  <span className="d-block flex0 tx-g600 tx-12">
                    {timenize(sound.metadata?.duration ?? 0)}
                  </span>
                </button>
              </li>
            ))}
          </ol>
        </Showmore>
      </div>
      <BuyModal
        category="album"
        data={album}
        isOpen={isBuyOpen}
        onClose={() => setBuyOpen(false)}
      />
    </div>
  )
}

export default AlbumMedia
